import { Component, ContentChildren, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChild } from '@angular/core';
import { PerfectScrollbarDirective } from 'ngx-perfect-scrollbar';
import { SortPipe } from '../../../pipes/sort.pipe';
import { SortInterface } from '../../sort-control/sort.interface';
import { ColumnComponent } from './column/column.component';

@Component({
  selector: 'app-sortable-table',
  templateUrl: './sortable-table.component.html',
  styleUrls: ['./sortable-table.component.css']
})
export class SortableTableComponent implements OnChanges {
  @Input() elements: any[];
  @Input() mainColumnIndex = 0;
  @Input() selectedOrder: SortInterface;
  @Input() rowClass: (value) => string = null;
  @Input() draggable = false;
  @Input() enableCopyOnDblClick = false;
  /**
   * Opt-in multi-row selection (Ctrl/Cmd+click toggles, Shift+click selects a
   * range, plain click selects a single row). Off by default so the ~60 other
   * consumers of this table are unaffected.
   */
  @Input() selectable = false;
  @Output() sort = new EventEmitter<SortInterface>();
  @Output() reorder = new EventEmitter<{ fromIndex: number, toIndex: number, element: any, selectedElements: any[] }>();
  @Output() cellCopied = new EventEmitter<string>();
  @Output() selectionChange = new EventEmitter<any[]>();
  @ContentChildren(ColumnComponent) columns: QueryList<ColumnComponent>;
  @ViewChild(PerfectScrollbarDirective) content: PerfectScrollbarDirective;

  draggedIndex: number = null;
  dragOverIndex: number = null;

  selectedElements: any[] = [];
  private lastSelectedElement: any = null;
  private sortPipe = new SortPipe();

  /**
   * Long-press machinery. When a cell whose column has `longPressable=true`
   * receives a mousedown, we start a timer. If the user releases (or moves
   * the mouse away) before the timer fires, `clicked` runs normally. If the
   * timer fires first, we mark `longPressFired` so the upcoming `click`
   * event is swallowed and emit `longPressed` instead.
   */
  private longPressTimer: any = null;
  private longPressFired = false;

  constructor(private hostElement: ElementRef) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.elements && this.content) {
      this.content.scrollToTop();
    }
  }

  /**
   * Clears the selection when the user clicks outside any data row of this
   * table (header, empty space or anywhere else on the page).
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.selectable || !this.selectedElements.length) {
      return;
    }
    const target = event.target as HTMLElement;
    const row = target && target.closest('tbody tr');
    if (row && this.hostElement.nativeElement.contains(row)) {
      return;
    }
    this.clearSelection();
  }

  getRowClass(row: any) {
    return (this.rowClass) ? this.rowClass(row) : '';
  }

  onDragStart(event: DragEvent, index: number, element: any) {
    if (!this.draggable) return;
    // When dragging a row that is not part of the current selection, drop the
    // selection so it behaves as a plain single-row drag.
    if (this.selectable && !this.isSelected(element)) {
      this.clearSelection();
    }
    this.draggedIndex = index;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', index.toString());
  }

  onDragOver(event: DragEvent, index: number) {
    if (!this.draggable || this.draggedIndex === null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    this.dragOverIndex = index;
  }

  onDragLeave(event: DragEvent) {
    this.dragOverIndex = null;
  }

  onDrop(event: DragEvent, toIndex: number, element: any) {
    if (!this.draggable || this.draggedIndex === null) return;
    event.preventDefault();
    const fromIndex = this.draggedIndex;
    if (fromIndex !== toIndex) {
      const isLastRow = toIndex === this.elements.length - 1;
      const isDraggingDown = fromIndex < toIndex;
      const finalToIndex = (isLastRow && isDraggingDown) ? toIndex + 1 : toIndex;
      this.reorder.emit({
        fromIndex,
        toIndex: finalToIndex,
        element,
        selectedElements: this.selectable ? [...this.selectedElements] : []
      });
    }
    this.draggedIndex = null;
    this.dragOverIndex = null;
  }

  onDragEnd(event: DragEvent) {
    this.draggedIndex = null;
    this.dragOverIndex = null;
  }

  getDragClass(index: number): string {
    if (!this.draggable) return '';
    if (this.draggedIndex === index) return 'dragging';
    if (this.dragOverIndex === index) return 'drag-over';
    return '';
  }

  onCellDblClick(event: MouseEvent) {
    if (!this.enableCopyOnDblClick) return;

    const target = event.target as HTMLElement;
    const td = target.closest('td') as HTMLElement;
    if (!td) return;

    // Copy the field's FULL value even when it is visually cut off. Columns that
    // truncate their text keep the complete value in a `title` attribute (article
    // number, material code, operations, observations, …); prefer it over the
    // rendered text, which would otherwise copy the "ASDSD…" cutoff.
    const text = this.getFullCellText(td);
    if (!text) return;

    // Copiar al portapapeles
    if ((navigator as any).clipboard) {
      (navigator as any).clipboard.writeText(text).then(() => {
        this.cellCopied.emit(text);
        this.showCopyFeedback(td);
      });
    } else {
      // Fallback para navegadores antiguos
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.cellCopied.emit(text);
      this.showCopyFeedback(td);
    }
  }

  /**
   * Returns the full text of a cell for copy-on-double-click. Truncated columns
   * store their complete value in a descendant `title` attribute (the cell
   * tooltip and/or the inner span used by article/material), so we prefer the
   * longest non-empty title and only fall back to the rendered (possibly
   * "…"-cut) text when there is none.
   */
  private getFullCellText(td: HTMLElement): string {
    const titledNodes = td.querySelectorAll('[title]');
    let best = '';
    for (let i = 0; i < titledNodes.length; i++) {
      const title = ((titledNodes[i] as HTMLElement).getAttribute('title') || '').trim();
      if (title.length > best.length) {
        best = title;
      }
    }
    return best.length > 0 ? best : (td.innerText || '').trim();
  }

  private showCopyFeedback(element: HTMLElement) {
    element.classList.add('cell-copied');
    setTimeout(() => {
      element.classList.remove('cell-copied');
    }, 300);
  }

  onCellMouseDown(column: ColumnComponent, element: any) {
    if (!column.longPressable) return;
    this.cancelLongPress();
    this.longPressFired = false;
    this.longPressTimer = setTimeout(() => {
      this.longPressFired = true;
      this.longPressTimer = null;
      column.longPressed.emit(element);
    }, column.longPressMillis);
  }

  onCellMouseUp() {
    this.cancelLongPress();
  }

  onCellClick(column: ColumnComponent, element: any) {
    // If the long-press already fired, swallow this click (it's the natural
    // mouseup → click that follows the hold) and reset the flag for next time.
    if (this.longPressFired) {
      this.longPressFired = false;
      return;
    }
    column.clicked.emit(element);
  }

  private cancelLongPress() {
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  isSelected(element: any): boolean {
    return this.selectedElements.indexOf(element) !== -1;
  }

  onRowClick(event: MouseEvent, element: any) {
    if (!this.selectable) return;
    // Let cell links navigate / copy without hijacking the click for selection.
    const target = event.target as HTMLElement;
    if (target && target.closest('a')) return;

    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      this.clearNativeTextSelection();
    }

    if (event.shiftKey && this.lastSelectedElement) {
      this.selectRangeTo(element);
    } else if (event.ctrlKey || event.metaKey) {
      this.toggleSelection(element);
      this.lastSelectedElement = element;
    } else {
      this.selectedElements = [element];
      this.lastSelectedElement = element;
    }
    this.selectionChange.emit([...this.selectedElements]);
  }

  private clearNativeTextSelection() {
    const selection = window.getSelection && window.getSelection();
    if (selection && selection.removeAllRanges) {
      selection.removeAllRanges();
    }
  }

  clearSelection() {
    if (!this.selectedElements.length) return;
    this.selectedElements = [];
    this.lastSelectedElement = null;
    this.selectionChange.emit([]);
  }

  private toggleSelection(element: any) {
    this.selectedElements = this.isSelected(element)
      ? this.selectedElements.filter((e) => e !== element)
      : [...this.selectedElements, element];
  }

  private selectRangeTo(element: any) {
    const ordered = this.selectedOrder
      ? this.sortPipe.transform(this.elements, this.selectedOrder)
      : (this.elements || []);
    const from = ordered.indexOf(this.lastSelectedElement);
    const to = ordered.indexOf(element);
    if (from === -1 || to === -1) {
      this.selectedElements = [element];
      return;
    }
    const [start, end] = from <= to ? [from, to] : [to, from];
    this.selectedElements = ordered.slice(start, end + 1);
  }

}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/shared/components/tables/sortable-table/sortable-table.component.ts